"use client";

import React, { useEffect, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { X } from "lucide-react";
import type { IListing } from "@/src/shared/types/listing";
import type { IFilter } from "@/src/shared/features/search";
import type { BlockedDate } from "@/src/shared/types/blockedDate";
import { useModalMotion } from "@/src/shared/hooks/useModalMotion";
import { useWindowSize } from "@/src/shared/hooks/useWindowSize";
import { cn } from "@/src/lib/utils";
import { listingService } from "../services";
import { IOS_PUSH } from "@/src/shared/constants/animation";
import { useSwipeBack } from "../hooks/useSwipeBack";
import ContactHostIntro from "./ContactHostIntro";
import MessageHostForm from "./MessageHostForm";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IContactHostDialogProps {
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  singleListingData: IListing;
  open: boolean;
  onClose: () => void;
  /** "Book" on the contact screen: the page opens its booking flow. */
  onBook?: () => void;
  handleUpdateQuery: () => void;
  blockedDates: BlockedDate[];
  checkoutOnlyDates?: string[];
}

/** How far the page underneath slides left while the next one covers it (iOS). */
const PARALLAX = 30;

/**
 * The app's contact flow from listing details, in two pages: Contact host (the host, the
 * listing's questions answered) then Message host (dates, guests, price and the message).
 * They move like iPhone pages: pushed in from the right with the page below sliding left,
 * and dragged right to go back. On a phone the flow itself is a pushed page (dragging the
 * first one right closes it); from `md` it sits in a centred dialog.
 */
const ContactHostDialog: React.FC<IContactHostDialogProps> = ({
  filters,
  setFilters,
  singleListingData,
  open,
  onClose,
  onBook,
  handleUpdateQuery,
  blockedDates,
  checkoutOnlyDates = [],
}) => {
  const { BACKDROP, PANEL } = useModalMotion();
  const { width } = useWindowSize();
  const isPhone = width > 0 && width < 768;
  const [showMessage, setShowMessage] = useState(false);

  // 0 = on screen, 1 = off to the right.
  const sheet = useMotionValue(isPhone ? 1 : 0);
  const message = useMotionValue(1);
  const sheetX = useTransform(sheet, (v) => `${v * 100}%`);
  const shade = useTransform(sheet, (v) => (1 - v) * 0.2);
  const messageX = useTransform(message, (v) => `${v * 100}%`);
  const contactX = useTransform(message, (v) => `${-PARALLAX * (1 - v)}%`);

  const datesLabel =
    filters.checkIn && filters.checkOut
      ? listingService.stayDatesLabel(
          new Date(filters.checkIn),
          new Date(filters.checkOut),
        )
      : null;

  // Every open starts on the contact page; on a phone it pushes in from the right.
  useEffect(() => {
    if (!open) return;
    setShowMessage(false);
    message.set(1);
    if (isPhone) {
      sheet.set(1);
      void animate(sheet, 0, IOS_PUSH);
    } else sheet.set(0);
  }, [open]);

  useLockBodyScroll(open);

  const close = () => {
    if (isPhone) void animate(sheet, 1, IOS_PUSH).then(onClose);
    else onClose();
  };
  const openMessage = () => {
    message.set(1);
    setShowMessage(true);
    void animate(message, 0, IOS_PUSH);
  };
  const closeMessage = () =>
    void animate(message, 1, IOS_PUSH).then(() => setShowMessage(false));

  // Dragging the contact page closes the flow on a phone; dragging Message host goes back.
  const swipeContact = useSwipeBack(sheet, onClose);
  const swipeMessage = useSwipeBack(message, () => setShowMessage(false));

  // Back on every phone page; in the desktop dialog only on Message host (the first page
  // has nothing to go back to, ✕ closes it).
  const nav = (onBack: () => void, label: string, backOnDesktop: boolean) => (
    <div className="flex items-center px-6 py-2 md:pt-4">
      <button
        type="button"
        onClick={onBack}
        aria-label={label}
        className={cn(
          "-ml-2 flex h-10 w-10 items-center justify-center",
          !backOnDesktop && "md:hidden",
        )}
      >
        <img
          src="/icons/listing/listing_page_back.svg"
          alt=""
          className="h-6 w-6"
        />
      </button>
      <button
        type="button"
        onClick={close}
        aria-label="Close"
        className="-mr-2 ml-auto hidden h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-[#F7F7F7] md:flex"
      >
        <X size={22} strokeWidth={1.5} />
      </button>
    </div>
  );

  const page =
    "absolute inset-0 flex touch-pan-y flex-col bg-white pt-[env(safe-area-inset-top)] md:pt-0";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[1000000] bg-black md:bg-black/40"
            style={isPhone ? { opacity: shade } : undefined}
            variants={isPhone ? undefined : BACKDROP}
            initial="initial"
            animate="animate"
            exit="exit"
            onClick={close}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={showMessage ? "Message host" : "Contact host"}
            className="fixed inset-0 z-[1000000] flex md:items-center md:justify-center md:p-8"
            style={isPhone ? { x: sheetX } : undefined}
            variants={isPhone ? undefined : PANEL}
            initial="initial"
            animate="animate"
            exit="exit"
            onClick={isPhone ? undefined : close}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative h-full w-full overflow-hidden bg-white md:h-[min(760px,90vh)] md:max-w-[720px] md:rounded-2xl md:shadow-[0_24px_64px_rgba(0,0,0,0.16)]"
            >
              <motion.div
                className={page}
                style={{ x: contactX }}
                {...(isPhone ? swipeContact : {})}
              >
                {nav(close, "Close", false)}
                <ContactHostIntro
                  listing={singleListingData}
                  datesLabel={datesLabel}
                  onMessageHost={openMessage}
                  onBook={() => {
                    onClose();
                    onBook?.();
                  }}
                />
              </motion.div>

              {showMessage && (
                <motion.div
                  className={`${page} shadow-[-8px_0_24px_rgba(0,0,0,0.08)]`}
                  style={{ x: messageX }}
                  {...swipeMessage}
                >
                  {nav(closeMessage, "Back", true)}
                  <MessageHostForm
                    listing={singleListingData}
                    filters={filters}
                    setFilters={setFilters}
                    datesLabel={datesLabel}
                    handleUpdateQuery={handleUpdateQuery}
                    blockedDates={blockedDates}
                    checkoutOnlyDates={checkoutOnlyDates}
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactHostDialog;
